import React, { useState, useContext } from 'react';
import { navigate } from '@reach/router';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';
import KeyIcon from '@mui/icons-material/VpnKeyOutlined';
import IntegrationIcon from '@mui/icons-material/IntegrationInstructionsOutlined';
import MarginRoundedIcon from '@mui/icons-material/MarginOutlined';
import ListAltRoundedIcon from '@mui/icons-material/ListAltOutlined';
import SmartToyIcon from '@mui/icons-material/SmartToyOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { ApplicationContext, canOpenPage, routes } from '@particles';
import { useMyAccess } from '@api';
import { signOutEverywhere } from '../../firebase/session';

const menuItems = [
  { label: 'My Profile', to: routes.SA_MyProfile, Icon: AccountCircleIcon },
  { label: 'Integrations', to: routes.SA_Settings, Icon: IntegrationIcon },
  { label: 'API Keys', to: routes.SA_ApiKeys, Icon: KeyIcon },
  { label: 'Markup Margins', to: routes.SA_MarkupMargins, Icon: MarginRoundedIcon },
  { label: 'AI Models', to: routes.SA_AiModels, Icon: SmartToyIcon },
  { label: 'Lookups', to: routes.SA_Lookups, Icon: ListAltRoundedIcon },
  { label: 'Roles & Staff', to: routes.SA_Roles, Icon: AdminPanelSettingsIcon }
];

function getInitials(user: { name?: string; email?: string } | undefined): string {
  if (!user) return '?';
  if (user.name && user.name.trim()) {
    const parts = user.name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (user.email && user.email.trim()) return user.email.slice(0, 2).toUpperCase();
  return '?';
}

const HeaderProfileMenu: React.FC = () => {
  const {
    state: { user },
    dispatch
  } = useContext(ApplicationContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Only the pages this person's role can open (Roles & Staff: Super Admin only).
  const { data: access } = useMyAccess(user?.id);
  const visibleItems = menuItems.filter(({ to }) => canOpenPage(to, access));
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (to: string) => {
    handleClose();
    navigate(to);
  };

  const handleSignOut = () => {
    handleClose();
    signOutEverywhere();
    dispatch({ key: 'user', value: undefined, type: 'set' });
    navigate('/login');
  };

  return (
    <>
      <ButtonBase
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          padding: '4px 8px 4px 4px',
          borderRadius: '999px',
          backgroundColor: 'rgba(0,0,0,0.06)',
          transition: 'background-color 180ms ease',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.09)' }
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontSize: '0.875rem',
            fontWeight: 600
          }}
        >
          {getInitials(user)}
        </Avatar>
        <KeyboardArrowDownIcon
          sx={{
            color: '#5f6b7c',
            fontSize: 18,
            transition: 'transform 180ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        />
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              minWidth: 220,
              mt: 1,
              borderRadius: '10px',
              border: '1px solid rgba(43, 34, 88, 0.08)',
              boxShadow: '0 1px 2px rgba(43, 34, 88, 0.05), 0 8px 24px rgba(43, 34, 88, 0.12)',
              '& .MuiMenuItem-root': {
                fontSize: 14,
                py: 1,
                fontFamily: 'Montserrat'
              }
            }
          }
        }}
      >
        {visibleItems.map(({ label, to, Icon }) => (
          <MenuItem key={to} onClick={() => handleItemClick(to)}>
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleSignOut} sx={{ color: '#B91C1C' }}>
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" sx={{ color: '#B91C1C' }} />
          </ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default HeaderProfileMenu;
